import React from 'react';
import { Calendar, CheckCircle, Clock } from 'lucide-react';
import { trainingSchedule } from '../data/schedule';

const SchedulePage = ({ setCurrentPage }) => {
  // Group schedule by month
  const months = ["Januari", "Februari", "Maret", "April", "Mei", "Juni"];
  const scheduleByMonth = months.reduce((acc, month) => {
    acc[month] = trainingSchedule.filter(s => s.month === month);
    return acc;
  }, {});

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 py-16 relative overflow-hidden transition-colors">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-sky-300 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8">
            <div className="inline-block bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4 backdrop-blur-sm">
              <Calendar className="w-4 h-4 inline mr-2" />
              Jadwal Pembelajaran
            </div>
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4 leading-tight">
              Kalender Training Data Science & AI
            </h1>
            <p className="text-lg text-blue-50 max-w-2xl mx-auto">
              Timeline pembelajaran terstruktur 6 bulan dengan materi yang dirancang untuk membangun keahlian Anda secara bertahap, dari fundamental hingga advanced.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center text-white">
              <div className="text-4xl font-bold mb-2">{trainingSchedule.length}</div>
              <p className="text-blue-100">Modul Pembelajaran</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center text-white">
              <div className="text-4xl font-bold mb-2">6</div>
              <p className="text-blue-100">Bulan Program</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center text-white">
              <div className="text-4xl font-bold mb-2">✓</div>
              <p className="text-blue-100">Sertifikasi Resmi</p>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Timeline Section */}
      <section className="py-20 bg-white transition-colors">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Monthly Schedule */}
          {months.map((month, monthIndex) => (
            <div key={month} className="mb-16">
              {/* Month Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="h-1 w-12 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full"></div>
                <h2 className="text-3xl font-bold text-blue-900">{month}</h2>
                <span className="ml-auto text-blue-600 font-semibold text-lg">
                  {scheduleByMonth[month].length} Materi
                </span>
              </div>

              {/* Timeline */}
              <div className="space-y-4">
                {scheduleByMonth[month].map((schedule, index) => (
                  <div
                    key={schedule.id}
                    className="group relative"
                  >
                    <div className="flex gap-6">
                      {/* Timeline marker */}
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-white font-bold shadow-lg group-hover:shadow-xl transition-shadow">
                          {index + 1}
                        </div>
                        {index < scheduleByMonth[month].length - 1 && (
                          <div className="w-1 h-12 bg-gradient-to-b from-blue-300 to-blue-100 mt-2"></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-4">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all group-hover:bg-blue-50">
                          {/* Date and Material */}
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Clock className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-semibold text-blue-600">
                                  {schedule.startDate} - {schedule.endDate}
                                </span>
                              </div>
                              <h3 className="text-xl font-bold text-blue-900">
                                {schedule.material}
                              </h3>
                            </div>
                            <div className="flex-shrink-0">
                              <div className="bg-white rounded-full p-3 shadow-sm">
                                <CheckCircle className="w-6 h-6 text-green-500" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-blue-100 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12 text-blue-900">
            Tentang Program Pembelajaran
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-white rounded-2xl p-8 border-2 border-blue-200 hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-xl font-bold text-blue-900 mb-3">Kurikulum Terstruktur</h3>
              <p className="text-blue-700">
                21 modul pembelajaran yang dirancang secara sistematis, mulai dari fundamental concepts hingga advanced techniques dalam Data Science & AI.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-2xl p-8 border-2 border-blue-200 hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-4">👨‍🏫</div>
              <h3 className="text-xl font-bold text-blue-900 mb-3">Mentor Berpengalaman</h3>
              <p className="text-blue-700">
                Dipandu oleh praktisi industri dengan pengalaman 10+ tahun, memberikan insight real-world dan best practices dalam setiap modul.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-2xl p-8 border-2 border-blue-200 hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-bold text-blue-900 mb-3">Sertifikasi Resmi</h3>
              <p className="text-blue-700">
                Dapatkan sertifikat resmi dari AIXpert dan partner institusi kami setelah menyelesaikan setiap modul pembelajaran.
              </p>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border-2 border-blue-200">
              <h3 className="text-lg font-bold text-blue-900 mb-4">✨ Fitur Program</h3>
              <ul className="space-y-3 text-blue-700">
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold mt-1">✓</span>
                  <span>Pembelajaran fleksibel online & offline</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold mt-1">✓</span>
                  <span>Materi berbasis case study dan project nyata</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold mt-1">✓</span>
                  <span>Tools dan platform teknologi terkini</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold mt-1">✓</span>
                  <span>Community dan networking dengan alumni</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-blue-200">
              <h3 className="text-lg font-bold text-blue-900 mb-4">🎯 Hasil yang Diharapkan</h3>
              <ul className="space-y-3 text-blue-700">
                <li className="flex items-start gap-3">
                  <span className="text-green-600 font-bold mt-1">✓</span>
                  <span>Menguasai tools Excel, SQL, Python, Google Sheets</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 font-bold mt-1">✓</span>
                  <span>Kemampuan data analysis dan visualization</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 font-bold mt-1">✓</span>
                  <span>Pengetahuan machine learning dan statistics</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 font-bold mt-1">✓</span>
                  <span>Siap untuk berkarir di dunia Data Analyst, Data Science & AI</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-blue-900 mb-6">
            Siap memulai perjalanan Anda di Data Science & AI?
          </h2>
          <button
            onClick={() => setCurrentPage('program')}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-10 py-4 rounded-xl text-lg hover:shadow-lg transition font-bold inline-flex items-center gap-2"
          >
            Lihat Program Bootcamp
            <span className="text-xl">→</span>
          </button>
        </div>
      </section>
    </div>
  );
};

export default SchedulePage;
